import { Button } from "@svea/design-system-components-react";
import { type FieldOption, ReactSelect, repeatArray, Text, tw, useText } from "@sveabank/cc-common";
import { useState } from "react";
import { v4 as getUuid } from "uuid";
import { usePageContext } from "../../hooks/context";
import { useApplicationId } from "../../hooks/useApplicationId";
import { useUtils } from "../../hooks/useUtils";
import type { DisplayedAddon, OnboardingAddon } from "../../types/product";
import { useChoosePackage } from "./hooks";

type Props = {
	addon: DisplayedAddon;
};

const AddonItemActions = ({ addon }: Props) => {
	const [amount, setAmount] = useState<number>(1);
	const [t, texts] = useText();

	const applicationId = useApplicationId();
	const { useSelectAddonMutation, useRemoveAddonMutation, selectedAddons } =
		usePageContext(useChoosePackage);

	const [selectAddonMutation, { isLoading: selectAddonsIsLoading }] = useSelectAddonMutation();
	const [removeAddonMutation, { isLoading: removeAddonsIsLoading }] = useRemoveAddonMutation();

	const { isMutationLoading } = useUtils();

	const selectedCards = selectedAddons?.filter(
		(product) => product.productDefinitionId === addon.definitionId && product.isAddon,
	);

	const selectAddons = async (addons: OnboardingAddon[]) => {
		await selectAddonMutation({
			applicationId,
			addons: addons.map((a) => ({
				productId: getUuid(),
				productDefinitionId: a.definitionId,
			})),
		}).unwrap();
	};

	const removeAddons = async (definitionId: OnboardingAddon["definitionId"]) => {
		await removeAddonMutation({
			applicationId,
			addonIds:
				selectedAddons
					?.filter((product) => product.productDefinitionId === definitionId)
					.map((product) => product.productId) ?? [],
		}).unwrap();
	};

	const isSelected = selectedAddons?.some((a) => a.productDefinitionId === addon.definitionId);

	const monthlyTotalFee = selectedCards.reduce(
		(acc, curr) => acc + Number(curr.monthlyFee.amount),
		0,
	);

	const isCheckCredit = addon.definitionId === "CHECKKREDIT";
	const addonTexts = texts.addons[addon.sku];

	const handleAmount = (value: FieldOption | null) => {
		if (value) setAmount(Number(value.value));
	};

	const options: FieldOption[] = [
		{ value: "1", label: "1" },
		{ value: "2", label: "2" },
		{ value: "3", label: "3" },
		{ value: "4", label: "4" },
		{ value: "5", label: "5" },
	];

	return !isSelected ? (
		<>
			<div className="flex justify-between gap-4">
				<span className={tw("w-16", isCheckCredit && "hidden")}>
					<ReactSelect
						aria-label={t(texts.chooseAddons.amountLabel, t(addonTexts.title))}
						options={[
							{ value: "1", label: "1" },
							{ value: "2", label: "2" },
							{ value: "3", label: "3" },
							{ value: "4", label: "4" },
							{ value: "5", label: "5" },
						]}
						defaultValue={options.find((o) => Number(o.value) === amount) || null}
						onChange={handleAmount}
					/>
				</span>

				<Button
					data-testid={`selectAddon-${addon.definitionId}`}
					data-tracking-action={`Onboarding: Addon selected - ${t(addonTexts.title)}`}
					data-tracking-name="Onboarding Addon"
					aria-label={t(texts.chooseAddons.addAddonLabel, t(addonTexts.title))}
					variant="primary"
					isDisabled={isSelected}
					onPress={() => !isMutationLoading && selectAddons(repeatArray(addon, Number(amount)))}
					isPending={selectAddonsIsLoading}
				>
					<Text bold className="text-text-contrast">
						{t(
							texts.chooseAddons.addAddonLabel,
							t("addButtonTitle" in addonTexts ? addonTexts.addButtonTitle : addonTexts.title),
						)}
					</Text>
				</Button>
			</div>
		</>
	) : (
		<div className="tablet:h-[52px] tablet:pt-0 flex items-center justify-between pt-4">
			<Text
				component="p"
				bold
				className="text-text-headline flex-1 text-lg"
				data-testid={`addon-cost-${addon.definitionId}`}
			>
				{isCheckCredit && `${t(texts.common.from)} `}
				{t(texts.chooseAddons.addonPricePerMonth, monthlyTotalFee.toString())}
			</Text>

			<div className="flex items-center justify-end gap-1">
				{!isCheckCredit && (
					<Text align="end" data-testid={`addon-amount-${addon.definitionId}`}>
						{`${selectedCards.length} ${t(texts.chooseAddons.extraCards)}`}
					</Text>
				)}
				<Button
					variant="icon"
					icon="trash"
					data-tracking-action={`Onboarding: Addon unselected - ${t(addonTexts.title)}`}
					data-tracking-name="Onboarding Addon"
					isPending={removeAddonsIsLoading}
					aria-label={t(texts.chooseAddons.removeAddonLabel, t(addonTexts.title))}
					onPress={() => !isMutationLoading && removeAddons(addon.definitionId)}
				/>
			</div>
		</div>
	);
};

export default AddonItemActions;
